import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounce, startWith } from 'rxjs/operators';
import { UserProfile } from '../models/model';
import { ApiService } from './api.services';
import { AuthService } from './auth.service';
import {
	EventService,
	EventServiceMessage,
	EventServiceType,
} from './event.service';
import { UserSessionService } from './user-session.service';

@Injectable({
	providedIn: 'root',
})
export class UserProfileService implements OnDestroy {
	private profileSubscription?: Subscription;
	private authSubscription?: Subscription;

	private userProfile?: UserProfile;

	public profileUpdated = new Subject<UserProfile>();

	constructor(
		private readonly eventService: EventService,
		private readonly apiService: ApiService,
		private readonly authService: AuthService
	) {
		this.authSubscription = this.authService.userSession().subscribe((it) => {
			this.updateUserProfile();
		});
	}

	requestInProcess = false;

	private async updateUserProfile(): Promise<UserProfile> {
		if (this.requestInProcess) {
			let waitCount = 0;
			while (!this.userProfile) {
				waitCount++;
				await new Promise((f) => setTimeout(f, 250));
				if (waitCount > 20) {
					console.error('Waiting too long for user profile!');
					throw new Error('Waiting too long for user profile!');
				}
			}
			return this.userProfile;
		}
		try {
			this.requestInProcess = true;
			this.userProfile = await this.apiService.getUserProfile(false);
			this.profileUpdated.next(this.userProfile);
			return this.userProfile;
		} finally {
			this.requestInProcess = false;
		}
	}

	clearUserProfile(): void {
		this.userProfile = undefined;
	}

	async getUserProfile(): Promise<UserProfile> {
		if (!this.userProfile) {
			return await this.updateUserProfile();
		}
		return this.userProfile;
	}

	ngOnDestroy(): void {
		this.profileSubscription?.unsubscribe();
		this.profileSubscription = undefined;
		this.authSubscription?.unsubscribe();
		this.authSubscription = undefined;
	}
}
